import React, { useContext, useState } from "react";
import style from "./index.module.scss";
import {
    Alert,
    Checkbox,
    Col,
    Input,
    InputNumber,
    message,
    Modal,
    Row,
    Spin
} from "antd";
import { ModalProps } from "antd/es/modal";
import Form from "rc-field-form";
import { ResultMessage } from "components/ResultMessage";
import { errorMessage } from "service/service";
import {
    autoGroup,
    getGroupPersonNumber,
    GroupType,
    previewOrExecuteRandomGroup
} from "service/group";
import { CourseContext } from "../../../../../Store";
import { GroupSettingsContext } from "../../../../store";
import SubmitError from "components/SubmitError";
import FieldWithError from "../../../../../../../components/FieldWithError";

interface AutoGroupModalProps extends ModalProps {
    onCancel: () => void;
    onAddOk: () => void; //成功的回调
    groupType: GroupType;
    randomGroupKey?: string;
    signInId?: number;
}

interface FormFields {
    groupCount: string; //小组数量
    groupName: string;
    autoSetGroupLeader: boolean; //是否设置组长
}

export default function AutoGroupModal({
    onCancel,
    groupType,
    randomGroupKey,
    signInId,
    onAddOk,
    ...props
}: AutoGroupModalProps) {
    const [form] = Form.useForm<FormFields>();
    const [num, setNum] = useState("0"); //人数
    const [checked, setChecked] = useState(true); //是否设为组长
    const { course } = useContext(CourseContext);
    const { updateNoGroupStudent } = useContext(GroupSettingsContext);

    const [loading, setLoading] = useState(false);

    // 提交表单
    const handleFinish = async (item: FormFields) => {
        const params = {
            courseNo: course.courseNo,
            courseSeq: course.courseSeq,
            weektimeId: course.weekTimeId,
            groupType: groupType,
            randomGroupKey,
            groupCount: item.groupCount,
            autoSetGroupLeader: item.autoSetGroupLeader ? "1" : "0"
        };
        setLoading(true);
        try {
            switch (groupType) {
                case GroupType.base:
                    await autoGroup(params);
                    break;
                case GroupType.random:
                    await previewOrExecuteRandomGroup({
                        courseNo: course.courseNo,
                        courseSeq: course.courseSeq,
                        weektimeId: course.weekTimeId,
                        signInId,
                        groupCount: parseInt(item.groupCount),
                        groupName: item.groupName,
                        randomGroupKey
                    });
                    break;
            }
            await updateNoGroupStudent();
            message.success("分组成功");
            onAddOk();
        } catch (e) {
            ResultMessage.error(
                <SubmitError title="分组失败" content={errorMessage(e)} />,
                { width: 300 }
            );
        } finally {
            setLoading(false);
        }
    };

    //获取未分组人数
    const { filterNoGroupList: noGroupStudent } =
        useContext(GroupSettingsContext);

    return (
        <Modal
            title="一键分组"
            maskClosable={false}
            {...props}
            className={style.root}
            onOk={form.submit}
            confirmLoading={loading}
            width={610}
            onCancel={onCancel}>
            <Alert
                message="说明：总学生数为未分组的学生数量，一键分组只针对无小组的学生进行分组。已分配小组的学生如需重新分组，请先删除原来小组或将学生从原组移出后重新分组"
                type="info"
                showIcon
                className={style.infoTip}
            />
            <Spin spinning={loading}>
                <div className={style.container}>
                    <Form
                        form={form}
                        onFinish={handleFinish}
                        initialValues={{
                            autoSetGroupLeader: true
                        }}
                        className={style.root}>
                        <Row className={style.row}>
                            <Col span={8} className={style.label}>
                                学生总数：
                            </Col>
                            <Col span={12} offset={1}>
                                {noGroupStudent?.length || 0} 人
                            </Col>
                        </Row>
                        <Row className={style.row}>
                            <Col span={8} className={style.label}>
                                分组数量：
                            </Col>
                            <Col span={12} offset={1}>
                                <Form.Field
                                    name="groupCount"
                                    initialValue={undefined}>
                                    <InputNumber
                                        min={0}
                                        max={noGroupStudent?.length || 0}
                                        onChange={e =>
                                            setNum(
                                                getGroupPersonNumber(
                                                    e,
                                                    noGroupStudent?.length
                                                )
                                            )
                                        }
                                        placeholder="请输入数量"
                                        style={{ width: 200 }}
                                    />
                                </Form.Field>
                            </Col>
                        </Row>
                        {groupType === GroupType.random && (
                            <Row className={style.row}>
                                <Col span={8} className={style.label}>
                                    分组名称：
                                </Col>
                                <Col span={8} offset={1}>
                                    <FieldWithError
                                        preserve={false}
                                        name="groupName">
                                        <Input
                                            style={{ width: 200 }}
                                            placeholder="不输入则自动生成名称"
                                        />
                                    </FieldWithError>
                                </Col>
                            </Row>
                        )}
                        <Row className={style.row}>
                            <Col span={8} className={style.label}>
                                每组人数：
                            </Col>
                            <Col span={12} offset={1}>
                                {num} 人
                            </Col>
                        </Row>
                        {groupType === GroupType.base && (
                            <Row className={style.row}>
                                <Col span={10} className={style.check}>
                                    <Form.Field
                                        name="autoSetGroupLeader"
                                        valuePropName="checked">
                                        <Checkbox
                                            onChange={e =>
                                                setChecked(e.target.checked)
                                            }>
                                            自动设置组长
                                        </Checkbox>
                                    </Form.Field>
                                </Col>
                            </Row>
                        )}
                    </Form>
                    {!checked && (
                        <Alert
                            message="取消自动设置组长，将需要您手动设置组长，否则无法发布小组活动"
                            type="warning"
                            className={style.alertTip}
                        />
                    )}
                </div>
            </Spin>
        </Modal>
    );
}
